import { parse } from 'graphql';
import { resolveRequestDocument } from 'graphql-request';

import type { RequestDocument } from 'graphql-request';

/**
 * Automatic Persisted Queries (APQ).
 *
 * Queries go out as a GET carrying only a SHA-256 hash of the document, so the
 * response is cacheable at the CDN. If the server has not seen the hash it
 * answers `PersistedQueryNotFound` and we register the document with a single
 * POST; every later request for that document is a bare GET.
 *
 * APQ is disabled in development (`NODE_ENV === 'development'`) because the
 * Next.js dev server does not cache fetch responses, HMR invalidates the
 * module-level hash caches, and React Strict Mode doubles requests — all of
 * which cause intermittent 500s during the hash-only GET → register POST
 * handshake. The skip is applied in the consumers (`requestServer.ts` and
 * `graphql-request.ts`) by bypassing `getPersistedQueryExtension` in dev.
 *
 * @see https://www.apollographql.com/docs/apollo-server/performance/apq
 */

/** Apollo Server only speaks version 1 of the protocol. */
const APQ_PROTOCOL_VERSION = 1;

const NOT_FOUND_CODE = 'PERSISTED_QUERY_NOT_FOUND';
const NOT_FOUND_MESSAGE = 'PersistedQueryNotFound';
const NOT_SUPPORTED_CODE = 'PERSISTED_QUERY_NOT_SUPPORTED';
const NOT_SUPPORTED_MESSAGE = 'PersistedQueryNotSupported';

/**
 * Apollo Server's CSRF prevention rejects a GET that carries no non-simple
 * header. Verified against the API: without this the request is a 400
 * "blocked as a potential Cross-Site Request Forgery"; with it, a 200.
 */
export const APQ_GET_HEADERS = { 'apollo-require-preflight': 'true' };

/**
 * Conservative ceiling for a hashed GET. CloudFront allows 8192 bytes across
 * the whole request line, but proxies in between are stricter; anything longer
 * falls back to POST rather than risking a 414.
 */
const MAX_GET_URL_LENGTH = 2000;

export interface PersistedQueryExtension {
  persistedQuery: {
    version: number;
    sha256Hash: string;
  };
}

/**
 * The GraphQL API's `formatError` flattens `extensions.code` onto the error
 * itself, so we accept both that shape and the standard Apollo one.
 */
export interface GraphQLErrorLike {
  message?: string;
  code?: string;
  extensions?: {
    code?: string;
  };
}

export interface ResolvedDocument {
  query: string;
  operationName?: string;
  isMutation: boolean;
}

/**
 * Documents come from module-level `gql` literals, so the set of distinct
 * documents is fixed at build time and these caches stay bounded. Caching the
 * parse here also means each document is only parsed once for the life of the
 * process rather than on every request.
 */
const documentCache = new Map<RequestDocument, ResolvedDocument>();
const hashCache = new Map<string, string>();

/** Test seam for the module-level caches above. */
export const clearApqCaches = () => {
  hashCache.clear();
  documentCache.clear();
};

const getOperationType = (query: string) => {
  try {
    const operation = parse(query).definitions.find(
      (definition) => definition.kind === 'OperationDefinition',
    );
    return operation && 'operation' in operation ? operation.operation : 'query';
  } catch {
    return 'query';
  }
};

/**
 * Normalises a document to the exact string that gets hashed and sent as
 * `query`. The hash must be taken over the string the server will parse, so
 * both always come from here.
 */
export const resolveDocument = (document: RequestDocument): ResolvedDocument => {
  const cached = documentCache.get(document);
  if (cached) {
    return cached;
  }

  const { query, operationName } = resolveRequestDocument(document);
  const resolved: ResolvedDocument = {
    query,
    operationName,
    isMutation: getOperationType(query) === 'mutation',
  };

  documentCache.set(document, resolved);
  return resolved;
};

const toHex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

/**
 * Hashes with WebCrypto, which exists on Node 18+ and in every browser on a
 * secure origin. Returns `null` where it is unavailable so callers can fall
 * back to sending the full document rather than failing the request.
 */
export const sha256 = async (input: string): Promise<string | null> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    return null;
  }

  try {
    return toHex(await subtle.digest('SHA-256', new TextEncoder().encode(input)));
  } catch {
    return null;
  }
};

/**
 * Builds the `extensions` payload for a query, or `undefined` where WebCrypto
 * is unavailable — in which case the caller sends the document as normal.
 */
export const getPersistedQueryExtension = async (
  query: string,
): Promise<PersistedQueryExtension | undefined> => {
  let sha256Hash = hashCache.get(query);

  if (!sha256Hash) {
    const computed = await sha256(query);
    if (!computed) {
      return undefined;
    }
    sha256Hash = computed;
    hashCache.set(query, sha256Hash);
  }

  return { persistedQuery: { version: APQ_PROTOCOL_VERSION, sha256Hash } };
};

/**
 * URL for a hashed GET, or `null` when the request must go over POST — a
 * mutation, or variables long enough to risk a 414.
 */
export const buildPersistedQueryUrl = (
  url: string,
  {
    operationName,
    variables,
    extensions,
    isMutation,
  }: {
    operationName?: string;
    variables?: Record<string, any>;
    extensions: PersistedQueryExtension;
    isMutation: boolean;
  },
): string | null => {
  if (isMutation) {
    return null;
  }

  const params: Array<[string, string]> = [];
  if (operationName) {
    params.push(['operationName', operationName]);
  }
  if (variables && Object.keys(variables).length > 0) {
    params.push(['variables', JSON.stringify(variables)]);
  }
  params.push(['extensions', JSON.stringify(extensions)]);

  const search = params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
  const target = `${url}${url.includes('?') ? '&' : '?'}${search}`;

  return target.length > MAX_GET_URL_LENGTH ? null : target;
};

const matchesApqError = (
  errors: Array<GraphQLErrorLike> | undefined,
  code: string,
  message: string,
) =>
  Array.isArray(errors) &&
  errors.some(
    (error) =>
      error?.extensions?.code === code || error?.code === code || error?.message === message,
  );

export const isPersistedQueryNotFound = (errors?: Array<GraphQLErrorLike>) =>
  matchesApqError(errors, NOT_FOUND_CODE, NOT_FOUND_MESSAGE);

export const isPersistedQueryNotSupported = (errors?: Array<GraphQLErrorLike>) =>
  matchesApqError(errors, NOT_SUPPORTED_CODE, NOT_SUPPORTED_MESSAGE);

/** Whether a hash-only attempt must be replayed with the full document. */
export const shouldRetryWithDocument = (errors?: Array<GraphQLErrorLike>) =>
  isPersistedQueryNotFound(errors) || isPersistedQueryNotSupported(errors);
